import React, { useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Upload } from 'lucide-react';
import { cn } from '@ideoz/ui';
import { workbenchStore } from '../../stores/workbench';
import { mediaLibrary, parseDataUri, storePathForUpload } from '../../preview-bridge/imageReplace';

/**
 * The pictures to choose from (Prototype Editor UX plan, step 6): every picture the prototype
 * already has, and one from the founder's own computer. Picking one shows it in the element at
 * once; Apply writes it.
 */
export type PickedImage = { storePath: string; reference: string; dataUri: string };

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

export function ImagePicker({
  current,
  onPick,
  className,
}: {
  /** The asset on show now, so it can be marked. */
  current?: string | null;
  onPick: (picked: PickedImage) => void;
  className?: string;
}): React.ReactElement {
  const assets = useStore(workbenchStore.assets);
  const fileRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const items = mediaLibrary(assets);

  const upload = (file: File) => {
    setProblem(null);
    if (!file.type.startsWith('image/')) {
      setProblem('That file is not a picture.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setProblem('Pictures over 4 MB slow the prototype down. Please pick a smaller one.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = String(reader.result ?? '');
      const parsed = parseDataUri(dataUri);
      if (!parsed) {
        setProblem('That picture could not be read.');
        return;
      }
      const storePath = storePathForUpload(file.name, assets, parsed.base64);
      onPick({ storePath, reference: `/${storePath.replace(/^public\//, '')}`, dataUri });
    };
    reader.onerror = () => setProblem('That picture could not be read.');
    reader.readAsDataURL(file);
  };

  return (
    <div className={cn('w-[232px] p-1', className)} data-testid="image-picker">
      {items.length > 0 ? (
        <div className="grid grid-cols-4 gap-1" role="listbox" aria-label="Pictures in this prototype">
          {items.map((item) => (
            <button
              key={item.storePath}
              type="button"
              role="option"
              aria-selected={item.storePath === current}
              title={item.name}
              className={cn(
                'aspect-square overflow-hidden rounded-md border bg-muted/40',
                item.storePath === current ? 'border-foreground ring-2 ring-foreground/20' : 'border-border hover:border-foreground/40',
              )}
              onClick={() => onPick({ storePath: item.storePath, reference: item.reference, dataUri: item.dataUri })}
            >
              <img src={item.dataUri} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : (
        <p className="px-1 py-1.5 text-[11px] text-muted-foreground">No pictures in this prototype yet.</p>
      )}
      <button
        type="button"
        className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-muted"
        onClick={() => fileRef.current?.click()}
      >
        <Upload size={12} aria-hidden />
        From your computer
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label="Choose a picture from your computer"
        data-testid="image-picker-upload"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = '';
        }}
      />
      {problem ? <p className="px-1 pt-1 text-[11px] text-red-600">{problem}</p> : null}
    </div>
  );
}
